import * as datefns from 'date-fns'
import { IssueEntity } from 'types/IssueEntity'
import { SourceType } from 'types/SourceType'

/**
 * Type can be a list of types, e.g. ['Publication', 'Terms'], or a single type, e.g. 'Publication'
 */
export function hasSourceOfType(issue: Pick<IssueEntity, 'sources'>, type: SourceType | SourceType[]): boolean {
    // if type is not a list, make it a list
    if (!Array.isArray(type)) {
        type = [type]
    }
    return issue.sources && issue.sources.some((source) => type.includes(source.sourceType))
}

/**
 * False if v is null, undefined, or an empty string, true otherwise.
 * @param {*} v value to check
 */
function hasValue(v) {
    return v !== null && v !== undefined && v !== ''
}

/**
 * Types can be a list of types, e.g. ['Publication', 'Terms'], or a single type, e.g. 'Publication'
 */
function checkMandatorySourceTypes(issue: IssueEntity, types: SourceType[] | SourceType, missingFields: string[]) {
    // if type is not a list, make it a list
    if (!Array.isArray(types)) {
        types = [types]
    }
    types.forEach((type) => {
        if (!hasSourceOfType(issue, type)) {
            missingFields.push(`source:${type}`)
        }
    })
}

function checkMandatoryFields(issue: IssueEntity, fields: string[] | string, missingFields: string[]) {
    // if fields is not a list, make it a list
    if (!Array.isArray(fields)) {
        fields = [fields]
    }
    fields.forEach((field) => {
        if (!hasValue(issue[field])) {
            missingFields.push(field)
        }
    })
}

function checkMandatoryCheckboxes(issue: IssueEntity, fields: string[] | string, missingFields: string[]) {
    // if fields is not a list, make it a list
    if (!Array.isArray(fields)) {
        fields = [fields]
    }
    fields.forEach((field) => {
        if (!issue[field]) {
            missingFields.push(field)
        }
    })
}

function checkAtLeastOneField(issue: IssueEntity, fields: string[], missingFields: string[]) {
    let found = false
    fields.forEach((field) => {
        if (hasValue(issue[field])) {
            found = true
        }
    })
    if (!found) {
        missingFields.push(...fields)
    }
}

export function checkInvestorsValues(issue: IssueEntity) {
    const missingFields: string[] = []

    if (issue.type === 'Guarantor fee issue') return []

    if (issue.type === 'Rights issue') {
        if (getRightsIssueStage(issue) >= 2) {
            checkMandatoryCheckboxes(issue, ['investorsAfterTerms'], missingFields)
        }

        if (getRightsIssueStage(issue) >= 3) {
            checkMandatoryCheckboxes(issue, ['investorsAfterProspectus'], missingFields)
        }
    }

    if ((issue.type === 'Rights issue' && getRightsIssueStage(issue) >= 3) || issue.type !== 'Rights issue') {
        if (!issue.noInvestorsNamed && !issue.investors?.length) {
            missingFields.push('investors')
        }
    }

    return missingFields
}

export const checkTermsValues = (issue: IssueEntity) => {
    switch (issue.type) {
        case 'IPO':
            return checkIpoTermsValues(issue)
        case 'Rights issue':
            return checkRightsIssueTermsValues(issue)
        case 'Directed issue':
            return checkDirectedIssueTermsFields(issue)
        case 'ABB Directed issue':
            return checkABBTermsFields(issue)
        case 'Non-cash issue':
            return checkNonCashTermsFields(issue)
    }

    return []
}

export const checkImportantDatesValues = (issue: IssueEntity) => {
    switch (issue.type) {
        case 'IPO':
            return checkIpoImportantDatesValues(issue)
        case 'Rights issue':
            return checkRightsIssueImportDatesValues(issue)
    }

    return []
}

const checkIpoImportantDatesValues = (issue: IssueEntity) => {
    const missingFields: string[] = []

    checkMandatoryFields(issue, ['ipoTradingDate', 'subStartDate', 'subEndDate'], missingFields)

    return missingFields
}

const checkIpoTermsValues = (issue: IssueEntity) => {
    const missingFields: string[] = []

    if (issue?.ipoData) {
        checkMandatoryFields(
            issue.ipoData,
            [
                'companyName',
                'instrumentSymbol',
                'instrumentIsin',
                'countryCode',
                'currency',
                'companyTotalShares',
                'marketplaceId',
            ],
            missingFields,
        )
    }

    checkMandatoryFields(issue, ['ipoData', 'offeredUnits', 'sharesInUnit'], missingFields)

    checkAtLeastOneField(issue, ['noOverAllotment', 'overAllotmentSize', 'overAllotmentSizeSecondary'], missingFields)
    checkAtLeastOneField(issue, ['noSecondaryOffering', 'secondaryOffering'], missingFields)

    return missingFields
}

/**
 * Checks presence of required fields for the prospectus stage.
 */
const checkIpoProspectusValues = (issue: IssueEntity) => {
    const missingFields: string[] = []
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkFeeStructureValues(issue))
    missingFields.push(...checkInvestorsValues(issue))

    checkMandatoryFields(issue.ipoData, ['instrumentIsin'], missingFields)

    return missingFields
}

export const checkOutcomeValues = (issue: IssueEntity) => {
    const missingFields: string[] = []
    switch (issue.type) {
        case 'IPO': {
            return checkIpoOutcomeValues(issue)
        }

        case 'Rights issue': {
            return checkRightsIssueOutcomeValues(issue)
        }

        case 'Guarantor fee issue':
        case 'Non-cash issue':
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
            break
        case 'ABB Directed issue':
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)

            if (!issue.noSecondaryOffering) {
                checkMandatoryFields(issue, ['secondarySub'], missingFields)
            }
            break
        case 'Directed issue': {
            checkMandatoryFields(issue, ['unitSubTotal'], missingFields)

            if (!issue.noSecondaryOffering) {
                checkMandatoryFields(issue, ['secondaryOffering', 'secondarySub'], missingFields)
            }
        }
    }

    return missingFields
}

const checkIpoOutcomeValues = (issue: IssueEntity) => {
    const missingFields: string[] = []

    if ((issue.offeredUnits ?? 0) > 0) {
        checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
    }

    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)

    if (!issue.noGuarantors) {
        checkAtLeastOneField(issue, ['unitSubGuarantor', 'unitPctSubGuarantor'], missingFields)
    }

    if (issue.overAllotmentSize) {
        checkMandatoryFields(issue, ['overAllotmentAllocation'], missingFields)
    }
    if (issue.overAllotmentSizeSecondary) {
        checkMandatoryFields(issue, ['overAllotmentAllocationSecondary'], missingFields)
    }

    if (!issue.noSecondaryOffering && !hasValue(issue.secondarySub)) {
        missingFields.push('secondarySub')
    }
    return missingFields
}

const checkRightsIssueImportDatesValues = (issue: IssueEntity) => {
    const missingFields: string[] = []
    const dateFields = [
        'recordDate',
        'incRightsDate',
        'exRightsDate',
        'rightsStartDate',
        'rightsEndDate',
        'subStartDate',
        'subEndDate',
    ]

    if (getRightsIssueStage(issue) < 2) return []

    checkMandatoryFields(issue, dateFields, missingFields)

    return missingFields
}

/**
 * Checks presence of required fields for the terms stage, i.e:
 * - sharesRequired
 * - rightsReceived
 * - rightsRequired
 * - unitsReceived
 * - sharesInUnit
 * - unitSubPrice
 * - offeredUnits
 */
const checkRightsIssueTermsValues = (issue: IssueEntity) => {
    const missingFields: string[] = []
    const termsFields = [
        'sharesRequired',
        'rightsReceived',
        'rightsRequired',
        'unitsReceived',
        'sharesInUnit',
        'unitSubPrice',
        'offeredUnits',
    ]

    if (getRightsIssueStage(issue) < 2) return []

    checkMandatoryFields(issue, termsFields, missingFields)
    if (!issue.noOverAllotment && !hasValue(issue.overAllotmentSize)) {
        missingFields.push('overAllotmentSize')
    }
    if (!issue.noSecondaryOffering && !hasValue(issue.secondaryOffering)) {
        missingFields.push('secondaryOffering')
    }
    return missingFields
}

export const checkFeeStructureValues = (issue: IssueEntity) => {
    switch (issue.type) {
        case 'Rights issue':
        case 'IPO': {
            if (issue.type === 'Rights issue' && getRightsIssueStage(issue) < 3) return []

            if (!issue.noGuarantors) {
                if (!hasValue(issue.fees) || issue.fees.length === 0) {
                    return ['fees']
                }
            }
        }
    }

    return []
}

/**
 * Checks presence of required fields for the prospectus stage.
 */
const checkRightsIssueProspectusValues = (issue: IssueEntity) => {
    const missingFields: string[] = []
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkFeeStructureValues(issue))
    missingFields.push(...checkInvestorsValues(issue))
    return missingFields
}

const checkRightsIssueOutcomeValues = (issue: IssueEntity) => {
    const missingFields: string[] = []

    if (getRightsIssueStage(issue) < 4) return []

    checkMandatoryFields(issue, ['unitSubTotal'], missingFields)
    checkAtLeastOneField(issue, ['unitSubWithRights', 'unitPctSubWithRights'], missingFields)
    checkAtLeastOneField(issue, ['unitSubWithoutRights', 'unitPctSubWithoutRights'], missingFields)

    if (!issue.noGuarantors) {
        checkAtLeastOneField(issue, ['unitSubGuarantor', 'unitPctSubGuarantor'], missingFields)
    }
    if (!issue.noSecondaryOffering && !hasValue(issue.secondarySub)) {
        missingFields.push('secondarySub')
    }
    if (!issue.noOverAllotment && !hasValue(issue.overAllotmentAllocation)) {
        missingFields.push('overAllotmentAllocation')
    }
    return missingFields
}

export const checkAdvisors = (issue: IssueEntity) => {
    const missingFields: string[] = []

    if (issue.type === 'Rights issue' && getRightsIssueStage(issue) < 3) return []

    if (!issue.noFinancialAdvisor) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'financial_advisor')) {
            missingFields.push('financialAdvisor')
        }
    }
    if (!issue.noLegalAdvisor) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'legal_advisor')) {
            missingFields.push('legalAdvisor')
        }
    }
    if (!issue.noIssuingAgent) {
        if (!issue.advisors || !issue.advisors.some((advisor) => advisor.type === 'issuing_agent')) {
            missingFields.push('issuingAgent')
        }
    }

    return missingFields
}

function getRightsIssueStage(issue: IssueEntity) {
    if (!issue.sources) throw new Error('Issue must have sources to determine stage.')
    let stage = 0
    if (hasSourceOfType(issue, 'Outcome')) {
        stage = 4
    } else if (hasSourceOfType(issue, ['Prospectus', 'Memorandum', 'InformationDocument'])) {
        stage = 3
    } else if (hasSourceOfType(issue, 'Terms') || (issue.type === 'Rights issue' && !issue.noTermsInitially)) {
        stage = 2
    } else if (hasSourceOfType(issue, 'Publication')) {
        stage = 1
    }

    return stage
}

const checkIpoAndRightsIssueSources = (issue: IssueEntity) => {
    const hasSomeOf: SourceType[] = ['Prospectus', 'Memorandum', 'InformationDocument']
    if (!hasSourceOfType(issue, hasSomeOf)) {
        return [hasSomeOf.map((t) => `source:${t}`).join(', ')]
    }

    return []
}

/**
 * Returns an object with the following keys:
 * - stage: 'initial', 'published', 'terms', 'prospect', 'outcome'
 * - status: 'complete', 'incomplete'
 * - missingFields: Array of field names that are missing
 */
function calculateIPOStatus(issue: IssueEntity) {
    let missingFields: string[] = []

    missingFields = missingFields.concat(checkIpoTermsValues(issue))
    missingFields = missingFields.concat(checkIpoImportantDatesValues(issue))
    missingFields = missingFields.concat(checkIpoProspectusValues(issue))
    missingFields = missingFields.concat(checkIpoOutcomeValues(issue))
    missingFields = missingFields.concat(checkIpoAndRightsIssueSources(issue))

    return { stage: 10, status: missingFields.length === 0 ? 'complete' : 'incomplete', missingFields }
}

function calculateRightsIssueStatus(issue: IssueEntity) {
    let status = 'incomplete'
    let missingFields: string[] = []
    let stage = getRightsIssueStage(issue)

    missingFields = missingFields.concat(checkRightsIssueTermsValues(issue))
    missingFields = missingFields.concat(checkRightsIssueImportDatesValues(issue))
    missingFields = missingFields.concat(checkRightsIssueProspectusValues(issue))
    missingFields = missingFields.concat(checkRightsIssueOutcomeValues(issue))

    if (stage >= 4) {
        missingFields = missingFields.concat(checkIpoAndRightsIssueSources(issue))
    }

    if (missingFields.length === 0) {
        status = 'complete'
        if (stage === 4) {
            stage = 10 // if outcome is complete, the issue is complete
        }
    }
    return { stage, status, missingFields }
}

const checkDirectedIssueTermsFields = (issue: IssueEntity) => {
    const missingFields: string[] = []
    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    return missingFields
}

function calculateDirectedIssueStatus(issue: IssueEntity) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkDirectedIssueTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))

    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    missingFields.push(...checkInvestorsValues(issue))
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

const checkABBTermsFields = (issue: IssueEntity) => {
    const missingFields: string[] = []
    checkMandatoryFields(issue, ['unitSubPrice'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    if (!issue.noSecondaryOffering) {
        checkMandatoryFields(issue, ['secondaryOffering'], missingFields)
    }

    return missingFields
}

function calculateABBIssueStatus(issue: IssueEntity) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkABBTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))

    missingFields.push(...checkInvestorsValues(issue))
    checkMandatorySourceTypes(issue, ['Publication', 'Outcome'], missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

const checkNonCashTermsFields = (issue: IssueEntity) => {
    const missingFields: string[] = []

    checkMandatoryFields(issue, ['unitSubPrice', 'noncashConsideration'], missingFields)
    checkAtLeastOneField(issue, ['offeredUnits', 'abbTarget'], missingFields)

    return missingFields
}

function calculateNonCashIssueStatus(issue: IssueEntity) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    missingFields.push(...checkAdvisors(issue))
    missingFields.push(...checkNonCashTermsFields(issue))
    missingFields.push(...checkOutcomeValues(issue))
    missingFields.push(...checkInvestorsValues(issue))
    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

function checkGuarantorIssueLinks(issue: IssueEntity, missingFields: string[]) {
    const groups = issue.issueGroups || []

    const thisIssueGroupIds = groups.filter((group) => group.issueId === issue.id).map((group) => group.groupId)
    const groupsMap = groups.reduce((acc, group) => {
        acc[group.groupId] = acc[group.groupId] || []
        acc[group.groupId].push(group)
        return acc
    }, {})
    const thisIssueGroups = thisIssueGroupIds.map((groupId) => groupsMap[groupId]).flat()
    if (
        !thisIssueGroups ||
        thisIssueGroups.length !== 2 ||
        !thisIssueGroups.some((group) => ['Rights issue', 'IPO'].includes(group.type)) ||
        !thisIssueGroups.some((group) => group.issueId === issue.id)
    ) {
        missingFields.push('guarantorGroup')
    }
}

function calculateGuarantorFeeIssueStatus(issue: IssueEntity) {
    const stage = 10
    const missingFields: string[] = []
    let status = 'complete'
    checkGuarantorIssueLinks(issue, missingFields)
    missingFields.push(...checkOutcomeValues(issue))
    checkMandatorySourceTypes(issue, 'Publication', missingFields)
    if (missingFields.length > 0) {
        status = 'incomplete'
    }
    return { stage, status, missingFields }
}

export function calculateIssueStatus(issue: IssueEntity) {
    if (!issue) return null
    if (hasSourceOfType(issue, 'Cancellation')) {
        return { stage: 5, status: 'complete', missingFields: [] }
    }
    let response = null
    if (issue.type === 'Rights issue') {
        response = calculateRightsIssueStatus(issue)
    } else if (issue.type === 'Directed issue') {
        response = calculateDirectedIssueStatus(issue)
    } else if (issue.type === 'ABB Directed issue') {
        response = calculateABBIssueStatus(issue)
    } else if (issue.type === 'Non-cash issue') {
        response = calculateNonCashIssueStatus(issue)
    } else if (issue.type === 'Guarantor fee issue') {
        response = calculateGuarantorFeeIssueStatus(issue)
    } else if (issue.type === 'IPO') {
        response = calculateIPOStatus(issue)
    }
    // make sure there are no duplicates in missingFields
    response.missingFields = Array.from(new Set(response.missingFields))

    if (issue.statusHideUntil) {
        // use datefns to check if the issue.statusHideUntil date is in the future
        const hideUntil = new Date(issue.statusHideUntil)
        if (datefns.isBefore(new Date(), hideUntil)) {
            response.hideUntil = hideUntil
        } else {
            response.hideUntil = null
        }
    }
    return response
}
